import { logger } from './logger';

interface EnvConfig {
  SUPABASE_URL: string;
  SUPABASE_ANON_KEY: string;
}

interface ValidationResult {
  valid: boolean;
  missing: string[];
  errors: string[];
}

function validateEnv(): ValidationResult {
  const result: ValidationResult = {
    valid: true,
    missing: [],
    errors: []
  };

  const requiredVars: (keyof EnvConfig)[] = ['SUPABASE_URL', 'SUPABASE_ANON_KEY'];

  // Check for missing variables
  for (const varName of requiredVars) {
    const value = import.meta.env[`VITE_${varName}`];
    if (!value || value.trim() === '') {
      result.missing.push(varName);
      result.valid = false;
    }
  }

  // Validate URL format
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  if (supabaseUrl && !supabaseUrl.startsWith('https://')) {
    result.errors.push('VITE_SUPABASE_URL must start with https://');
    result.valid = false;
  }

  // Validate key format (should be a JWT-like string)
  const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (supabaseKey && supabaseKey.length < 100) {
    result.errors.push('VITE_SUPABASE_ANON_KEY appears to be invalid (too short)');
    result.valid = false;
  }

  return result;
}

export function validateEnvironment(): boolean {
  const validation = validateEnv();

  if (!validation.valid) {
    const errorMessage = `Environment Configuration Error:\n` +
      `Missing: ${validation.missing.map(v => `VITE_${v}`).join(', ')}\n` +
      `Errors: ${validation.errors.join(', ')}`;
    
    logger.error('Environment Configuration Error', {
      missing: validation.missing.map(v => `VITE_${v}`),
      errors: validation.errors
    }, 'EnvValidation');
    
    // Show user-visible error (wait for DOM if needed)
    if (typeof window !== 'undefined') {
      const showError = () => {
        if (document.body) {
          // Create a visible error overlay
          const errorDiv = document.createElement('div');
          errorDiv.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            background: #dc2626;
            color: white;
            padding: 1rem;
            z-index: 99999;
            font-family: monospace;
            font-size: 14px;
            line-height: 1.5;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          `;
          errorDiv.textContent = errorMessage;
          document.body.appendChild(errorDiv);
        } else {
          // DOM not ready yet, wait for it
          if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', showError);
          } else {
            // Fallback: use setTimeout to wait a bit
            setTimeout(showError, 100);
          }
        }
      };
      showError();
    }
    
    // Fail fast - throw error to prevent app from running with invalid config
    throw new Error(errorMessage);
  }

  if (import.meta.env.DEV) {
    logger.info('Environment configuration valid', undefined, 'EnvValidation');
  }

  return true;
}

// Feature Flags - Phase 2 features behind flags
export function isFeatureEnabled(feature: string): boolean {
  const flagValue = import.meta.env[`VITE_FEATURE_${feature.toUpperCase()}`];
  return flagValue === 'true' || flagValue === '1';
}

// Phase 2 feature flags
export const FEATURES = {
  VALUE_PROP_CARDS: 'VALUE_PROP_CARDS',
  ACCORDION: 'ACCORDION'
} as const;

// Auto-validate on import
validateEnvironment();
